import { decompressLZF } from 'packages/lidar-tool/utils/pcd-loader/decompress-lzf';
import { LoaderUtils } from 'three';
import { parseHeader } from 'packages/lidar-tool/utils/pcd-loader/parse-header';

const littleEndian = true;

/** The PCD content after parsing */
export interface PCDContent {
  position: Float32Array;
  // Present in PCDs generated after 5/29/2025:
  lidarInfo?: Uint8Array; // each entry is an integer ranging from 0 to n-1, where n is # of lidar sensors on vehicle (4 for Gen2)
  // Present in Radar PCDs only:
  intensity?: Float32Array;
  velocity?: number[];
  sensorComp?: number[][];
  sensorPoint?: number[][];
}

/**
 * Based on https://github.com/mrdoob/three.js/blob/master/examples/jsm/loaders/PCDLoader.js
 * better version that it wasn't based on https://github.com/pmndrs/three-stdlib/blob/main/src/loaders/PCDLoader.js
 * note both make assumptions about the fields present that our file does not have.
 * note this is not a general purpose PCD parser, it does require some adapting
 * to our files.
 *
 * See also: https://gitlab.com/taketwo/three-pcd-loader/blob/master/decompress-lzf.js
 */
export const parsePCDBuffer = (data: ArrayBuffer | string): PCDContent => {
  const textData = LoaderUtils.decodeText(new Uint8Array(data as ArrayBuffer));

  // parse header (always ascii format)
  const pcdHeader = parseHeader(textData);

  // parse data
  const position: number[] = [];
  const lidarInfo: number[] = [];
  const intensity: number[] = [];
  const velocity: number[] = [];
  const sensorComp: number[][] = [];
  const sensorPoints: number[][] = [];

  // ascii
  if (pcdHeader.data === 'ascii' && typeof data === 'string') {
    const { offset } = pcdHeader;
    const pcdData = textData.substr(pcdHeader.headerLen);
    const lines = pcdData.split('\n');
    for (let i = 0, l = lines.length; i < l; i++) {
      if (lines[i] === '') continue;
      const line = lines[i].split(' ');
      if (offset.x !== undefined) {
        position.push(parseFloat(line[offset.x]));
        position.push(parseFloat(line[offset.y]));
        position.push(parseFloat(line[offset.z]));
      }
    }
  }

  // binary-compressed

  // normally data in PCD files are organized as array of structures: XYZRGBXYZRGB
  // binary compressed PCD files organize their data as structure of arrays: XXYYZZRGBRGB
  // that requires a totally different parsing approach compared to non-compressed data

  if (pcdHeader.data === 'binary_compressed' && typeof data !== 'string') {
    // Bug fix: when a PCD contains 0 points, PCD might be missing the 8 size bytes that are expected to immediately follow the header. Handle this gracefully.
    if (pcdHeader.headerLen + 8 >= data.byteLength) {
      if (pcdHeader.points === 0) {
        console.warn('Ignored empty PCD', pcdHeader);
        const emptyPCD: PCDContent = {
          position: new Float32Array(),
        };
        return emptyPCD;
      }
      console.error('PCD failed to parse: ', pcdHeader);
      throw new Error(
        'binary_compressed PCD is missing compressedSize, decompressedSize bytes after header, but has ' +
          pcdHeader.points +
          ' points.'
      );
    }
    const sizes = new Uint32Array(
      data.slice(pcdHeader.headerLen, pcdHeader.headerLen + 8)
    );
    const [compressedSize, decompressedSize] = sizes;
    const decompressed = decompressLZF(
      new Uint8Array(data, pcdHeader.headerLen + 8, compressedSize),
      decompressedSize
    );
    const dataview = new DataView(decompressed.buffer);
    const { offset, points, type, sizeMap } = pcdHeader;
    for (let i = 0; i < pcdHeader.points; i++) {
      if (offset.x !== undefined) {
        position.push(
          dataview.getFloat32(
            points * offset.x + pcdHeader.size[0] * i,
            littleEndian
          )
        );
        position.push(
          dataview.getFloat32(
            points * offset.y + pcdHeader.size[1] * i,
            littleEndian
          )
        );
        position.push(
          dataview.getFloat32(
            points * offset.z + pcdHeader.size[2] * i,
            littleEndian
          )
        );
      }

      if (
        offset.lidar_info !== undefined &&
        type?.lidar_info === 'U' &&
        sizeMap.lidar_info === 1
      ) {
        lidarInfo.push(
          dataview.getUint8(points * offset.lidar_info + sizeMap.lidar_info * i)
        );
      }

      if (
        offset.intensity !== undefined &&
        type?.intensity === 'U' &&
        sizeMap.intensity === 1
      ) {
        intensity.push(
          dataview.getUint8(points * offset.intensity + sizeMap.intensity * i)
        );
      }

      // Parse velocity range in case this is a radar PCD. Use float 64 because size map allocates 8 bytes.
      if (offset.range_velocity !== undefined) {
        velocity.push(
          dataview.getFloat64(
            points * offset.range_velocity + sizeMap.range_velocity * i,
            littleEndian
          )
        );
      }

      // Parse sensor compensated values in case this is a radar PCD.
      if (offset.vx_comp_sensor !== undefined) {
        const x = dataview.getFloat64(
          points * offset.vx_comp_sensor + sizeMap.vx_comp_sensor * i,
          littleEndian
        );
        const y = dataview.getFloat64(
          points * offset.vy_comp_sensor + sizeMap.vy_comp_sensor * i,
          littleEndian
        );
        const z = dataview.getFloat64(
          points * offset.vz_comp_sensor + sizeMap.vz_comp_sensor * i,
          littleEndian
        );
        sensorComp.push([x, y, z]);
      }

      // Parse sensor values in case this is a radar PCD.
      if (offset.vx_sensor !== undefined) {
        const x = dataview.getFloat64(
          points * offset.vx_sensor + sizeMap.vx_sensor * i,
          littleEndian
        );
        const y = dataview.getFloat64(
          points * offset.vy_sensor + sizeMap.vy_sensor * i,
          littleEndian
        );
        const z = dataview.getFloat64(
          points * offset.vz_sensor + sizeMap.vz_sensor * i,
          littleEndian
        );
        sensorPoints.push([x, y, z]);
      }
    }
  }

  // binary
  if (pcdHeader.data === 'binary' && typeof data !== 'string') {
    const dataview = new DataView(data, pcdHeader.headerLen);
    const { offset, type, sizeMap } = pcdHeader;
    for (
      let i = 0, row = 0;
      i < pcdHeader.points;
      i++, row += pcdHeader.rowSize
    ) {
      if (offset.x !== undefined) {
        position.push(dataview.getFloat32(row + offset.x, littleEndian));
        position.push(dataview.getFloat32(row + offset.y, littleEndian));
        position.push(dataview.getFloat32(row + offset.z, littleEndian));
      }

      // We expect lidar_info to be an Unsigned byte, if present
      if (
        offset.lidar_info &&
        type?.lidar_info === 'U' &&
        sizeMap.lidar_info === 1
      ) {
        lidarInfo.push(dataview.getUint8(row + offset.lidar_info));
      }

      if (
        offset.intensity &&
        type?.intensity === 'U' &&
        sizeMap.intensity === 1
      ) {
        intensity.push(dataview.getUint8(row + offset.intensity));
      }
    }
  }

  const content: PCDContent = {
    position: new Float32Array(position),
    lidarInfo: lidarInfo.length > 0 ? new Uint8Array(lidarInfo) : undefined,
    intensity: intensity.length > 0 ? new Float32Array(intensity) : undefined,
    velocity: velocity.length > 0 ? velocity : undefined,
    sensorComp: sensorComp.length > 0 ? sensorComp : undefined,
    sensorPoint: sensorPoints.length > 0 ? sensorPoints : undefined,
  };
  return content;
};
